// src/pages/departments/Departments.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Loader2, Pencil, Trash2, Search, AlertCircle, Check, X } from 'lucide-react';
import { departmentService } from '../../services/departmentService';
import { useAuth } from '../../context/AuthContext';

const EMPTY_FORM = {
  department_name: '',
  department_code: '',
  manager_name: '',
  description: '',
};

const inputCls = 'w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors';

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    {children}
  </div>
);

const Departments = () => {
  const { user } = useAuth();
  const canAccess = user && (user.role === 'super_admin' || user.role === 'hr_manager');

  // List state
  const [departments, setDepartments] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isEdit, setIsEdit] = useState(false);
  const [editCode, setEditCode] = useState('');   // department_code store hoga
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  // Load departments
  const loadDepartments = async () => {
    setListLoading(true);
    setListError('');
    try {
      const response = await departmentService.getDepartments();
      const data = response?.data || [];
      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      setListError(err?.response?.data?.detail || err.message || 'Failed to load departments');
      setDepartments([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { loadDepartments(); }, []);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  // Submit - create or update
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!form.department_name?.trim()) return setFormError('Department name is required.');
    if (!form.department_code?.trim()) return setFormError('Department code is required.');
    if (!form.manager_name?.trim()) return setFormError('Manager name is required.');

    setSaving(true);
    try {
      const payload = {
        department_name: form.department_name.trim(),
        department_code: form.department_code.trim(),
        manager_name: form.manager_name.trim(),
        description: form.description?.trim() || '',
      };

      if (isEdit) {
        // editCode = original department_code
        await departmentService.updateDepartment(editCode, payload);
        setFormSuccess('Department updated successfully.');
      } else {
        await departmentService.createDepartment(payload);
        setFormSuccess('Department created successfully.');
      }

      setTimeout(async () => {
        await loadDepartments();
        closeForm();
      }, 1000);
    } catch (err) {
      setFormError(err?.response?.data?.detail || err.message || 'Failed to save department.');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async (deptCode, deptName) => {
    if (!window.confirm(`Delete department "${deptName}"? This cannot be undone.`)) return;
    setDeletingId(deptCode);
    try {
      await departmentService.deleteDepartment(deptCode);
      await loadDepartments();
    } catch (err) {
      setListError(err?.response?.data?.detail || err.message || 'Failed to delete department');
    } finally {
      setDeletingId('');
    }
  };

  const openNewForm = () => {
    setForm(EMPTY_FORM);
    setIsEdit(false);
    setEditCode('');
    setFormError('');
    setFormSuccess('');
    setShowForm(true);
  };

  const openEditForm = (dept) => {
    setForm({
      department_name: dept.name || '',
      department_code: dept.department_code || '',
      manager_name: dept.head || '',
      description: dept.description || '',
    });
    setIsEdit(true);
    setEditCode(dept.department_code);   // yahan department_code save hota hai
    setFormError('');
    setFormSuccess('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setIsEdit(false);
    setEditCode('');
    setFormError('');
    setFormSuccess('');
  };

  const filtered = departments.filter(d =>
    !search ||
    d.name?.toLowerCase().includes(search.toLowerCase()) ||
    d.department_code?.toLowerCase().includes(search.toLowerCase()) ||
    d.head?.toLowerCase().includes(search.toLowerCase())
  );

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
        You do not have permission to access this page.
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Departments</h1>
          <p className="text-sm text-gray-500 mt-0.5">Create and manage company departments</p>
        </div>
        {!showForm && (
          <button
            onClick={openNewForm}
            className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium px-4 py-2.5 rounded-control transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Department
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-card shadow-soft border border-gray-100 mb-6">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800">
              {isEdit ? 'Edit Department' : 'New Department'}
            </h2>
            <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {formError && (
              <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-control bg-red-50 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {formError}
              </div>
            )}
            {formSuccess && (
              <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-control bg-green-50 text-green-700 text-sm">
                <Check className="w-4 h-4 mt-0.5 shrink-0" />
                {formSuccess}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <Field label="Department Name" required>
                <input
                  className={inputCls}
                  value={form.department_name}
                  onChange={(e) => setField('department_name', e.target.value)}
                  placeholder="e.g. Engineering"
                />
              </Field>
              <Field label="Department Code" required>
                <input
                  className={inputCls}
                  value={form.department_code}
                  onChange={(e) => setField('department_code', e.target.value)}
                  placeholder="e.g. ENG-01"
                  disabled={isEdit}   // edit mein code change nahi hoga
                />
              </Field>
              <Field label="Manager / Head" required>
                <input
                  className={inputCls}
                  value={form.manager_name}
                  onChange={(e) => setField('manager_name', e.target.value)}
                  placeholder="Manager name"
                />
              </Field>
              <Field label="Description">
                <input
                  className={inputCls}
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder="Short description"
                />
              </Field>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeForm}
                className="px-5 py-2.5 rounded-control border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 bg-primary hover:bg-primary-dark text-white text-sm font-medium px-5 py-2.5 rounded-control transition-colors disabled:opacity-60"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {isEdit ? 'Update Department' : 'Create Department'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {!showForm && (
        <div>
          {/* Search */}
          <div className="mb-4 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, code or manager..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>

          {listError && (
            <div className="mb-4 px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">
              {listError}
            </div>
          )}

          {listLoading && (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}

          {!listLoading && filtered.length === 0 && (
            <div className="bg-white rounded-card shadow-soft border border-gray-100 p-12 text-center">
              <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No departments found.</p>
            </div>
          )}

          {!listLoading && filtered.length > 0 && (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block bg-white rounded-card shadow-soft border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      <th className="px-5 py-3">Department Name</th>
                      <th className="px-5 py-3">Code</th>
                      <th className="px-5 py-3">Manager / Head</th>
                      <th className="px-5 py-3">Employees</th>
                      <th className="px-5 py-3">Description</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((d) => (
                      <tr key={d.department_code} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                        <td className="px-5 py-3.5 font-medium text-gray-800">{d.name}</td>
                        <td className="px-5 py-3.5 text-gray-500 text-xs font-mono">{d.department_code}</td>
                        <td className="px-5 py-3.5 text-gray-600">{d.head || '—'}</td>
                        <td className="px-5 py-3.5 text-gray-600">{d.total_employees ?? 0}</td>
                        <td className="px-5 py-3.5 text-gray-500 text-xs max-w-xs truncate">{d.description || '—'}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              onClick={() => openEditForm(d)}
                              className="text-gray-400 hover:text-primary transition-colors"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(d.department_code, d.name)}
                              disabled={deletingId === d.department_code}
                              className="text-gray-400 hover:text-red-500 disabled:opacity-40 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {filtered.map((d) => (
                  <div key={d.department_code} className="bg-white rounded-card shadow-soft border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-medium text-gray-800 text-sm">{d.name}</h3>
                      <span className="text-xs text-gray-400 font-mono">{d.department_code}</span>
                    </div>
                    {d.head && <p className="text-xs text-gray-600 mb-1">Head: {d.head}</p>}
                    <p className="text-xs text-gray-500 mb-1">Employees: {d.total_employees ?? 0}</p>
                    {d.description && <p className="text-xs text-gray-500 mb-3">{d.description}</p>}
                    <div className="flex items-center gap-4 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => openEditForm(d)}
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-primary"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(d.department_code, d.name)}
                        disabled={deletingId === d.department_code}
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-red-500 disabled:opacity-40"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Departments;